import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Input from "../../../../componentes/input/input";
import Textarea from "../../../../componentes/input/textarea";
import Button from "../../../../componentes/button/button";
import { CustomDropdown } from "../../../../componentes/dropdown/CustomDropdown";
import TipoServicoTIService from "../../../../services/resource/tipoServicoTIService";
import NovaSolicitacaoLayout from "../shared/NovaSolicitacaoLayout";
import { useRascunhoFormulario } from "../../Rascunho/RascunhoContext";
import { listaDoRascunho, mesclarComPadrao } from "../../../../utils/solicitacoes/rascunhoStorage";
import { enviarAnexosStaged } from "../../../../utils/solicitacoes/anexosStaged";
import { enviarComentariosStaged } from "../../../../utils/solicitacoes/comentariosStaged";

const tipoServicoService = new TipoServicoTIService();

const SIM_NAO_OPTIONS = [
    { value: "true", label: "Sim" },
    { value: "false", label: "Não" },
];

const inicial = {
    resumo: "",
    descricaoDetalhada: "",
    tipoServicoId: null,
    valoresCampos: {},
};

export default function NovaChamadoTIForm({ service, etapaInicialExigeAnexo, onCriada, encaminhar }) {
    const [form, setForm] = useState(inicial);
    const [anexos, setAnexos] = useState([]);
    const [comentarios, setComentarios] = useState([]);

    // Rascunho (botão "Salvar rascunho" da página) — só campos, anexos ficam de fora.
    useRascunhoFormulario(
        () => ({ form, comentarios }),
        dados => {
            setForm(mesclarComPadrao(inicial, dados.form));
            setComentarios(listaDoRascunho(dados.comentarios));
        },
    );
    const [tiposServico, setTiposServico] = useState([]);
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        tipoServicoService.listar().then(res => setTiposServico(res.data)).catch(() => setTiposServico([]));
    }, []);

    const tiposServicoAtivos = useMemo(() => tiposServico.filter(t => t.ativo), [tiposServico]);
    const tipoServicoSelecionado = useMemo(
        () => tiposServico.find(t => t.id === form.tipoServicoId) || null,
        [tiposServico, form.tipoServicoId]
    );
    const camposDinamicos = useMemo(() => tipoServicoSelecionado?.campos || [], [tipoServicoSelecionado]);
    // Campos do tipo IMAGEM não têm valor em valoresCampos — viram anexos comuns da solicitação,
    // exibidos/exigidos pelo próprio painel de Anexos (StagedAnexosEditor), não neste grid.
    const camposFormulario = useMemo(() => camposDinamicos.filter(c => c.tipoCampo !== "IMAGEM"), [camposDinamicos]);
    const camposImagem = useMemo(() => camposDinamicos.filter(c => c.tipoCampo === "IMAGEM"), [camposDinamicos]);
    const imagensObrigatorias = useMemo(() => camposImagem.filter(c => c.obrigatorio).map(c => c.rotulo), [camposImagem]);
    const imagensOpcionais = useMemo(() => camposImagem.filter(c => !c.obrigatorio).map(c => c.rotulo), [camposImagem]);

    function set(campo, valor) {
        setForm(prev => ({ ...prev, [campo]: valor }));
    }

    function setValorCampo(chave, valor) {
        setForm(prev => ({ ...prev, valoresCampos: { ...prev.valoresCampos, [chave]: valor } }));
    }

    function selecionarTipoServico(tipoServicoId) {
        setForm(prev => ({ ...prev, tipoServicoId, valoresCampos: {} }));
    }

    function validar() {
        if (!form.resumo.trim() || !form.descricaoDetalhada.trim() || !form.tipoServicoId) {
            toast.error("Preencha todos os campos obrigatórios.");
            return false;
        }
        for (const campo of camposFormulario) {
            const valor = form.valoresCampos[campo.chave];
            if (campo.obrigatorio && (valor === undefined || valor === null || valor === "")) {
                toast.error(`Preencha o campo "${campo.rotulo}".`);
                return false;
            }
        }
        if (imagensObrigatorias.length > 0) {
            const faltando = imagensObrigatorias.filter(
                rotulo => !anexos.some(item => item.descricao?.trim().toLowerCase() === rotulo.toLowerCase())
            );
            if (faltando.length > 0) {
                toast.error(`Anexe uma imagem para: ${faltando.join(", ")}.`);
                return false;
            }
        } else if (etapaInicialExigeAnexo && anexos.length === 0) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return false;
        }
        return true;
    }

    async function enviar() {
        if (!validar()) return;

        let usuarioEscolhidoId;
        try {
            usuarioEscolhidoId = await encaminhar("ETAPA_2_ANALISE_TI");
        } catch {
            return;
        }

        setEnviando(true);
        service.criarSolicitacao({
            resumo: form.resumo,
            descricaoDetalhada: form.descricaoDetalhada,
            tipoServicoId: form.tipoServicoId,
            valoresCampos: form.valoresCampos,
            usuarioEscolhidoId,
        })
            .then(async res => {
                const [{ falha: falhaAnexos }, { falha: falhaComentarios }] = await Promise.all([
                    enviarAnexosStaged(res.data.id, anexos),
                    enviarComentariosStaged(res.data.id, comentarios),
                ]);
                if (falhaAnexos.length > 0 || falhaComentarios.length > 0) {
                    const partes = [];
                    if (falhaAnexos.length > 0) partes.push(`${falhaAnexos.length} anexo(s) (${falhaAnexos.map(f => `${f.arquivo.name}: ${f.erro}`).join(", ")})`);
                    if (falhaComentarios.length > 0) partes.push(`${falhaComentarios.length} comentário(s)`);
                    toast.error(`Chamado aberto, mas houve falha ao enviar: ${partes.join(" e ")}.`);
                }
                onCriada(res.data);
            })
            .catch(error => console.error("Erro ao abrir chamado de TI", error))
            .finally(() => setEnviando(false));
    }

    return (
        <NovaSolicitacaoLayout
            anexos={anexos}
            onAnexosChange={setAnexos}
            comentarios={comentarios}
            onComentariosChange={setComentarios}
            etapaInicialExigeAnexo={etapaInicialExigeAnexo}
            anexosObrigatorios={imagensObrigatorias}
            sugestoes={[...imagensOpcionais, "Outros"]}
        >
            <Input label="Resumo" required placeholder="Breve resumo do chamado" value={form.resumo} onChange={e => set("resumo", e.target.value)} />

            <Textarea label="Descrição detalhada" required value={form.descricaoDetalhada} onChange={e => set("descricaoDetalhada", e.target.value)} rows={4} />

            <div>
                <label className="text-sm font-medium text-foreground/80 block mb-1.5">Tipo de Serviço <span className="text-destructive">*</span></label>
                <CustomDropdown
                    value={form.tipoServicoId}
                    onChange={e => selecionarTipoServico(e.value)}
                    options={tiposServicoAtivos}
                    optionValue="id"
                    optionLabel="nome"
                    placeholder="Selecione"
                />
            </div>

            {camposFormulario.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border border-border bg-muted/30 p-4">
                    {camposFormulario.map(campo => (
                        <CampoDinamicoInput
                            key={campo.chave}
                            campo={campo}
                            valor={form.valoresCampos[campo.chave] ?? ""}
                            onChange={valor => setValorCampo(campo.chave, valor)}
                        />
                    ))}
                </div>
            )}

            {camposImagem.length > 0 && (
                <p className="text-xs text-muted-foreground">
                    <i className="fa fa-info-circle mr-1" />
                    Este tipo de serviço pede imagem(ns) — anexe no painel de Anexos, ao lado.
                </p>
            )}

            <div className="flex justify-end">
                <Button variant="blue" label="Abrir Chamado" iconLeft="fa fa-paper-plane" loading={enviando} onClick={enviar} />
            </div>
        </NovaSolicitacaoLayout>
    );
}

function CampoDinamicoInput({ campo, valor, onChange }) {
    const opcoes = (campo.opcoes || []).map(o => ({ value: o, label: o }));

    switch (campo.tipoCampo) {
        case "TEXTO_LONGO":
            return (
                <div className="sm:col-span-2">
                    <Textarea label={campo.rotulo} required={campo.obrigatorio} value={valor} onChange={e => onChange(e.target.value)} rows={3} />
                </div>
            );
        case "NUMERO":
            return <Input type="number" label={campo.rotulo} required={campo.obrigatorio} value={valor} onChange={e => onChange(e.target.value)} />;
        case "DATA":
            return <Input type="date" label={campo.rotulo} required={campo.obrigatorio} value={valor} onChange={e => onChange(e.target.value)} />;
        case "BOOLEANO":
            return (
                <div>
                    <label className="text-sm font-medium text-foreground/80 block mb-1.5">{campo.rotulo} {campo.obrigatorio && <span className="text-destructive">*</span>}</label>
                    <CustomDropdown value={valor} onChange={e => onChange(e.value)} options={SIM_NAO_OPTIONS} placeholder="Selecione" />
                </div>
            );
        case "SELECAO":
            return (
                <div>
                    <label className="text-sm font-medium text-foreground/80 block mb-1.5">{campo.rotulo} {campo.obrigatorio && <span className="text-destructive">*</span>}</label>
                    <CustomDropdown value={valor} onChange={e => onChange(e.value)} options={opcoes} placeholder="Selecione" />
                </div>
            );
        case "LOCALIZACAO":
            return <CampoLocalizacao campo={campo} valor={valor} onChange={onChange} />;
        default:
            return <Input label={campo.rotulo} required={campo.obrigatorio} value={valor} onChange={e => onChange(e.target.value)} />;
    }
}

function CampoLocalizacao({ campo, valor, onChange }) {
    const [capturando, setCapturando] = useState(false);

    function capturar() {
        if (!navigator.geolocation) {
            toast.error("Este navegador não suporta captura de localização.");
            return;
        }
        setCapturando(true);
        navigator.geolocation.getCurrentPosition(
            pos => {
                onChange(`${pos.coords.latitude},${pos.coords.longitude}`);
                setCapturando(false);
            },
            () => {
                toast.error("Não foi possível obter a localização — verifique a permissão do navegador.");
                setCapturando(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    }

    return (
        <div>
            <label className="text-sm font-medium text-foreground/80 block mb-1.5">{campo.rotulo} {campo.obrigatorio && <span className="text-destructive">*</span>}</label>
            {valor ? (
                <div className="flex items-center gap-2 border border-input rounded-md h-9 px-3 text-sm">
                    <i className="fa fa-map-marker-alt text-primary shrink-0" />
                    <span className="flex-1 truncate text-foreground">{valor}</span>
                    <button type="button" onClick={() => onChange("")} className="shrink-0 text-destructive hover:bg-destructive/10 rounded-full p-1" title="Limpar">
                        <i className="fa fa-times" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={capturar}
                    disabled={capturando}
                    className="h-9 w-full flex items-center justify-center gap-2 border border-input rounded-md text-sm text-foreground/80 hover:bg-muted transition-colors duration-150 disabled:opacity-60"
                >
                    <i className={`fa ${capturando ? "fa-spinner fa-spin" : "fa-location-arrow"}`} />
                    {capturando ? "Obtendo localização..." : "Capturar localização atual"}
                </button>
            )}
        </div>
    );
}
